import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Wand2 } from 'lucide-react';
import { Button, Modal, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { ThemeDocThumbnail } from '@/components/theme/ThemeDocThumbnail';
import { forkTheme } from '../api/themes';
import { usePresets } from '../hooks/useThemeDoc';

/**
 * The blank-page killer: a fresh `/studio` opens onto official presets to
 * remix. Picking one forks it into the user's own draft (preset rows stay
 * immutable); "Start blank" just closes the gallery.
 */
export function PresetGallery() {
  const navigate = useNavigate();
  const { data: presets = [], isLoading } = usePresets();
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  if (!open || (!isLoading && presets.length === 0)) return null;

  async function start(presetId: string): Promise<void> {
    setBusy(presetId);
    try {
      const draft = await forkTheme(presetId);
      navigate(`${ROUTES.themeStudio}?t=${draft.id}`, { replace: true });
    } catch (e) {
      toast((e as Error).message || 'Could not open preset', 'error');
      setBusy(null);
    }
  }

  return (
    <Modal open onClose={() => setOpen(false)} className="max-w-2xl">
      <div className="mb-1 flex items-center gap-2 text-lg font-extrabold text-hi">
        <Sparkles size={18} className="text-accent" /> Start from a preset
      </div>
      <p className="mb-5 text-[12.5px] text-mid">
        Official starting points — pick one and make it yours, or start from scratch.
      </p>

      {isLoading ? (
        <div className="grid place-items-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busy != null}
              onClick={() => void start(p.id)}
              className="group overflow-hidden rounded-xl border border-white/10 text-left transition-all hover:-translate-y-0.5 hover:border-white/30 disabled:opacity-60"
            >
              <ThemeDocThumbnail doc={p.doc} className="aspect-video" />
              <span className="block px-3 py-2">
                <span className="block truncate text-[13px] font-bold text-hi">
                  {busy === p.id ? 'Opening…' : p.name}
                </span>
                {p.description && <span className="block truncate text-[11px] text-lo">{p.description}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <Button variant="ghost" className="gap-1.5" onClick={() => setOpen(false)}>
          <Wand2 size={14} /> Start blank
        </Button>
      </div>
    </Modal>
  );
}
